import { AUTH_USER_STORAGE_KEY, readStoredAuthSession } from "@/lib/auth-storage";
import { isWalletCloudMode } from "@/lib/runtime-mode";
import { scopedStorageKey } from "@/lib/user-scope";

export type UISettings = {
  hub_base: string;
  api_key: string;
  tenant_id: string;
  hf_token: string;
  refresh_sec: number;
};

const SETTINGS_KEY = "nebula.ui.settings.v1";

const DEFAULT_SETTINGS: UISettings = {
  hub_base: "",
  api_key: "",
  tenant_id: "",
  hf_token: "",
  refresh_sec: 10,
};

export function readUISettings(userID?: string): UISettings {
  if (typeof window === "undefined") return { ...DEFAULT_SETTINGS };
  try {
    const raw = window.localStorage.getItem(scopedStorageKey(SETTINGS_KEY, userID));
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(raw) as Partial<UISettings>;
    return {
      hub_base: String(parsed.hub_base ?? "").trim(),
      api_key: String(parsed.api_key ?? "").trim(),
      tenant_id: String(parsed.tenant_id ?? "").trim(),
      hf_token: String(parsed.hf_token ?? "").trim(),
      refresh_sec: Number.isFinite(Number(parsed.refresh_sec))
        ? Number(parsed.refresh_sec)
        : DEFAULT_SETTINGS.refresh_sec,
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function writeUISettings(next: UISettings, userID?: string) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(scopedStorageKey(SETTINGS_KEY, userID), JSON.stringify(next));
}

export class APIError extends Error {
  status: number;
  code: string;
  detail: string;

  constructor(status: number, code: string, detail: string) {
    super(detail || code || `HTTP ${status}`);
    this.status = status;
    this.code = code || `HTTP_${status}`;
    this.detail = detail || "Request failed";
  }
}

export type APIRequestOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  rawBody?: BodyInit;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  timeoutMs?: number;
  cache?: "default" | "no-store";
  cacheMaxAgeMs?: number;
};

type APIResponseCacheEnvelope = {
  createdAt: number;
  payload: unknown;
};

const API_RESPONSE_CACHE_PREFIX = "genrl.api-response-cache.v1";
const API_RESPONSE_CACHE_MAX_AGE_MS = 30 * 60 * 1000;
const API_RESPONSE_CACHE_MAX_BYTES = 512 * 1024;

function normalizeBaseURL(value: string) {
  return String(value || "")
    .trim()
    .replace(/\/$/, "");
}

function runtimeEnvAPIBaseURL() {
  return normalizeBaseURL(String(import.meta.env.VITE_API_BASE_URL || ""));
}

function settingsAPIBaseURL(settings: UISettings) {
  if (isWalletCloudMode()) return "";
  return normalizeBaseURL(settings.hub_base);
}

function normalizedAPIPathname(path: string): string {
  const raw = String(path || "").trim();
  if (!raw) return "/";
  try {
    if (/^https?:\/\//i.test(raw)) return new URL(raw).pathname || "/";
  } catch {
    // Fall back to string parsing below.
  }
  const withoutHash = raw.split("#", 1)[0] || "";
  const withoutQuery = withoutHash.split("?", 1)[0] || "";
  return withoutQuery.startsWith("/") ? withoutQuery : `/${withoutQuery}`;
}

function apiPathUsesStoredAccountScope(path: string): boolean {
  const pathname = normalizedAPIPathname(path);
  if (pathname === "/admin" || pathname.startsWith("/admin/")) return false;
  if (pathname === "/api/auth" || pathname.startsWith("/api/auth/")) return false;
  if (pathname === "/api/v1/auth" || pathname.startsWith("/api/v1/auth/")) return false;
  if (pathname === "/admin/v1/auth" || pathname.startsWith("/admin/v1/auth/")) return false;
  if (pathname === "/admin/v1/customer" || pathname.startsWith("/admin/v1/customer/")) return false;
  if (pathname === "/platform/v1" || pathname.startsWith("/platform/v1/")) return false;
  return true;
}

function apiPathUsesStoredSessionToken(path: string): boolean {
  const pathname = normalizedAPIPathname(path);
  if (pathname === "/admin" || pathname.startsWith("/admin/")) return false;
  if (pathname === "/api/auth" || pathname.startsWith("/api/auth/")) return false;
  if (pathname === "/admin/v1/auth" || pathname.startsWith("/admin/v1/auth/")) return false;
  if (pathname === "/admin/v1/customer" || pathname.startsWith("/admin/v1/customer/")) return false;
  if (pathname === "/platform/v1" || pathname.startsWith("/platform/v1/")) return false;
  switch (pathname) {
    case "/api/v1/auth/signup":
    case "/api/v1/auth/login/challenge":
    case "/api/v1/auth/login/identity-backup":
    case "/api/v1/auth/login/verify":
    case "/api/v1/auth/google/config":
    case "/api/v1/auth/google":
    case "/api/v1/auth/google/exchange":
      return false;
    default:
      return true;
  }
}

function activeUserIDHint() {
  if (typeof window === "undefined") return "";
  try {
    const raw = window.localStorage.getItem(AUTH_USER_STORAGE_KEY);
    if (!raw) return "";
    const parsed = JSON.parse(raw) as { id?: unknown };
    return String(parsed?.id || "").trim();
  } catch {
    return "";
  }
}

function isLocalhostHost(hostname: string) {
  const host = String(hostname || "").toLowerCase();
  return host === "127.0.0.1" || host === "localhost";
}

function addLocalDevAPIBaseURLs(add: (value: string) => void) {
  add("http://127.0.0.1:8080");
  add("http://localhost:8080");
  add("http://127.0.0.1:38080");
  add("http://127.0.0.1:38081");
  add("http://127.0.0.1:38082");
  add("http://localhost:38080");
  add("http://localhost:38081");
  add("http://localhost:38082");
}

function resolveBaseURLCandidates(settings: UISettings): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const add = (value: string) => {
    const normalized = normalizeBaseURL(value);
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    out.push(normalized);
  };

  const cloudMode = isWalletCloudMode();
  add(settingsAPIBaseURL(settings));
  if (!cloudMode) {
    add(runtimeEnvAPIBaseURL());
  }

  if (typeof window !== "undefined") {
    add(window.location.origin);
    const host = window.location.hostname || "";
    if (!cloudMode) {
      addLocalDevAPIBaseURLs(add);
      if (!isLocalhostHost(host)) {
        add("http://127.0.0.1:8081");
        add("http://127.0.0.1:8082");
        add("http://localhost:8081");
        add("http://localhost:8082");
      }
    }
  } else if (cloudMode) {
    // Server-side callers may use an explicitly injected upstream. Browsers
    // are deliberately same-origin only: falling back from a company gateway
    // to GENRL's default backend would cross an isolation boundary.
    add(runtimeEnvAPIBaseURL());
  }

  if (out.length === 0) add("");
  return out;
}

export function resolveAPIBaseURLCandidates(
  settingsOverride?: UISettings,
  userID?: string,
): string[] {
  const settings = settingsOverride ?? readUISettings(userID);
  return resolveBaseURLCandidates(settings);
}

export function resolveAPIBaseURL(settingsOverride?: UISettings, userID?: string): string {
  return resolveAPIBaseURLCandidates(settingsOverride, userID)[0] || "";
}

function buildURL(path: string, baseURL: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  const base = normalizeBaseURL(baseURL);
  if (!path.startsWith("/")) path = `/${path}`;
  return `${base}${path}`;
}

export function buildAPIURL(path: string, settingsOverride?: UISettings, userID?: string): string {
  return buildURL(path, resolveAPIBaseURL(settingsOverride, userID));
}

function apiCacheUserScope(settings: UISettings) {
  return activeUserIDHint() || settings.tenant_id || "guest";
}

function apiResponseCacheKey(method: string, path: string, baseURL: string, settings: UISettings) {
  return scopedStorageKey(
    `${API_RESPONSE_CACHE_PREFIX}:${method.toUpperCase()}:${buildURL(path, baseURL)}`,
    apiCacheUserScope(settings),
  );
}

function apiPathIsCacheable(path: string) {
  const normalized = normalizedAPIPathname(path).toLowerCase();
  if (!normalized) return false;
  return !(
    normalized === "/api/auth" ||
    normalized.startsWith("/api/auth/") ||
    normalized === "/api/v1/auth" ||
    normalized.startsWith("/api/v1/auth/")
  );
}

function emitAPIRequestFailure(path: string, method: string, error: APIError) {
  if (typeof window === "undefined") return;
  const pathname = normalizedAPIPathname(path);
  if (pathname === "/api/v1/frontend/errors") return;
  if (
    error.status < 500 &&
    error.status !== 408 &&
    error.code !== "ERR_NETWORK" &&
    error.code !== "ERR_TIMEOUT"
  ) {
    return;
  }
  window.dispatchEvent(
    new CustomEvent("genrl:api-error", {
      detail: {
        error,
        path: pathname,
        method: method.toUpperCase(),
        status: error.status,
        code: error.code,
      },
    }),
  );
}

function readAPIResponseCache<T>(
  method: string,
  path: string,
  baseCandidates: string[],
  settings: UISettings,
  maxAgeMs = API_RESPONSE_CACHE_MAX_AGE_MS,
): T | null {
  if (typeof window === "undefined") return null;
  const now = Date.now();
  for (const baseURL of baseCandidates) {
    try {
      const raw = window.localStorage.getItem(apiResponseCacheKey(method, path, baseURL, settings));
      if (!raw) continue;
      const envelope = JSON.parse(raw) as Partial<APIResponseCacheEnvelope>;
      const createdAt = Number(envelope.createdAt || 0);
      if (!createdAt || now - createdAt > maxAgeMs) continue;
      return envelope.payload as T;
    } catch {
      // Ignore malformed cache entries; the live request path remains authoritative.
    }
  }
  return null;
}

function writeAPIResponseCache(
  method: string,
  path: string,
  baseURL: string,
  settings: UISettings,
  payload: unknown,
) {
  if (typeof window === "undefined") return;
  try {
    const serialized = JSON.stringify({
      createdAt: Date.now(),
      payload,
    } satisfies APIResponseCacheEnvelope);
    if (serialized.length > API_RESPONSE_CACHE_MAX_BYTES) return;
    window.localStorage.setItem(apiResponseCacheKey(method, path, baseURL, settings), serialized);
  } catch {
    // Cache is best-effort and should never block the UI.
  }
}

export function readCachedAPIResponse<T = unknown>(
  path: string,
  settingsOverride?: UISettings,
  maxAgeMs?: number,
): T | null {
  const settings = settingsOverride ?? readUISettings();
  return readAPIResponseCache<T>(
    "GET",
    path,
    resolveBaseURLCandidates(settings),
    settings,
    maxAgeMs,
  );
}

async function parseResponseBody(res: Response): Promise<unknown> {
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    return res.json();
  }
  const text = await res.text();
  return text;
}

export async function apiRequest<T = any>(
  path: string,
  options: APIRequestOptions = {},
  settingsOverride?: UISettings,
): Promise<T> {
  const settings = settingsOverride ?? readUISettings();
  const method = options.method || "GET";
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(options.headers || {}),
  };
  const useStoredAccountScope = apiPathUsesStoredAccountScope(path);
  const useStoredSessionToken = apiPathUsesStoredSessionToken(path);

  if (useStoredAccountScope && settings.api_key) headers["X-API-Key"] = settings.api_key;
  if (useStoredAccountScope && settings.tenant_id) headers["X-Tenant-Id"] = settings.tenant_id;
  if (useStoredAccountScope && !headers["X-Tenant-Id"]) {
    const hintedUserID = activeUserIDHint();
    if (hintedUserID) headers["X-Tenant-Id"] = hintedUserID;
  }
  if (useStoredSessionToken && !headers.Authorization) {
    const session = readStoredAuthSession();
    if (session?.token) headers.Authorization = `Bearer ${session.token}`;
  }

  let body: BodyInit | undefined;
  if (options.rawBody !== undefined) {
    body = options.rawBody;
  } else if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options.body);
  }

  const controller = new AbortController();
  const signal = options.signal ?? controller.signal;
  const timeoutMs = options.timeoutMs ?? 30000;
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const hasExplicitBase = !!normalizeBaseURL(
    settingsAPIBaseURL(settings) ||
      (!isWalletCloudMode() || typeof window === "undefined" ? runtimeEnvAPIBaseURL() : ""),
  );
  const baseCandidates = hasExplicitBase
    ? [resolveAPIBaseURL(settings)]
    : resolveBaseURLCandidates(settings);
  const cacheable =
    options.cache !== "no-store" &&
    method === "GET" &&
    options.body === undefined &&
    options.rawBody === undefined &&
    apiPathIsCacheable(path);
  let lastError: APIError | null = null;

  try {
    for (let idx = 0; idx < baseCandidates.length; idx += 1) {
      const candidate = baseCandidates[idx];
      try {
        const res = await fetch(buildURL(path, candidate), {
          method,
          headers,
          body,
          signal,
          credentials: "include",
          cache: options.cache ?? "no-store",
        });

        const payload = await parseResponseBody(res);
        if (!res.ok) {
          if (payload && typeof payload === "object") {
            const p = payload as Record<string, unknown>;
            throw new APIError(
              res.status,
              String(p.error_code || p.code || "HTTP_ERROR"),
              String(p.error_detail || p.detail || p.message || `HTTP ${res.status}`),
            );
          }
          throw new APIError(
            res.status,
            "HTTP_ERROR",
            typeof payload === "string" ? payload : `HTTP ${res.status}`,
          );
        }

        if (cacheable) writeAPIResponseCache(method, path, candidate, settings, payload);
        return payload as T;
      } catch (err) {
        if (err instanceof APIError) {
          lastError = err;
          const canFallback =
            !hasExplicitBase &&
            idx < baseCandidates.length - 1 &&
            (err.status === 404 || err.code === "ERR_NETWORK" || err.code === "ERR_TIMEOUT");
          if (canFallback) continue;
          throw err;
        }
        if (err instanceof DOMException && err.name === "AbortError") {
          lastError = new APIError(408, "ERR_TIMEOUT", "Request timed out");
        } else {
          lastError = new APIError(
            500,
            "ERR_NETWORK",
            err instanceof Error ? err.message : "Network request failed",
          );
        }
        const canFallback = !hasExplicitBase && idx < baseCandidates.length - 1;
        if (canFallback) continue;
        throw lastError;
      }
    }
    throw lastError ?? new APIError(500, "ERR_NETWORK", "Network request failed");
  } catch (err) {
    const failure =
      err instanceof APIError
        ? err
        : err instanceof DOMException && err.name === "AbortError"
          ? new APIError(408, "ERR_TIMEOUT", "Request timed out")
          : new APIError(
              500,
              "ERR_NETWORK",
              err instanceof Error ? err.message : "Network request failed",
            );
    emitAPIRequestFailure(path, method, failure);
    if (cacheable) {
      const cached = readAPIResponseCache<T>(
        method,
        path,
        baseCandidates,
        settings,
        options.cacheMaxAgeMs ?? API_RESPONSE_CACHE_MAX_AGE_MS,
      );
      if (cached !== null) {
        if (
          failure.status === 408 ||
          failure.status >= 500 ||
          failure.code === "ERR_NETWORK" ||
          failure.code === "ERR_TIMEOUT"
        ) {
          return cached;
        }
      }
    }
    throw failure;
  } finally {
    clearTimeout(timer);
  }
}

export function financeUserID(fallback = "") {
  const settings = readUISettings();
  return settings.tenant_id || fallback || "";
}
